import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';

import { texts } from '@/lib/texts';
import { LoginPage } from '@/routes/LoginPage';
import { PlaceholderPage } from '@/routes/PlaceholderPage';

import { AuthGuard } from './AuthGuard';
import { Layout } from './Layout';
import { VenueLayout } from './VenueLayout';

/**
 * Страницы грузятся по маршруту (Stage 15, задача 4): графики `/metrics` и таблицы
 * не попадают в стартовый чанк; `/login` и плейсхолдер — в основном.
 */
function page(load: () => Promise<ComponentType>): LazyExoticComponent<ComponentType> {
  return lazy(() => load().then((component) => ({ default: component })));
}

const ActivitiesPage = page(() => import('@/routes/ActivitiesPage').then((m) => m.ActivitiesPage));
const ActivityPage = page(() => import('@/routes/ActivityPage').then((m) => m.ActivityPage));
const CitiesPage = page(() => import('@/routes/CitiesPage').then((m) => m.CitiesPage));
const CompaniesPage = page(() => import('@/routes/CompaniesPage').then((m) => m.CompaniesPage));
const FlagsPage = page(() => import('@/routes/FlagsPage').then((m) => m.FlagsPage));
const MatchingPage = page(() => import('@/routes/MatchingPage').then((m) => m.MatchingPage));
const MetricsPage = page(() => import('@/routes/MetricsPage').then((m) => m.MetricsPage));
const NotificationsPage = page(() =>
  import('@/routes/NotificationsPage').then((m) => m.NotificationsPage),
);
const OrdersPage = page(() => import('@/routes/OrdersPage').then((m) => m.OrdersPage));
const PoolPage = page(() => import('@/routes/PoolPage').then((m) => m.PoolPage));
const ProposalsPage = page(() => import('@/routes/ProposalsPage').then((m) => m.ProposalsPage));
const SeasonBuilderPage = page(() =>
  import('@/routes/SeasonBuilderPage').then((m) => m.SeasonBuilderPage),
);
const SeasonsPage = page(() => import('@/routes/SeasonsPage').then((m) => m.SeasonsPage));
const SeatsPage = page(() => import('@/routes/SeatsPage').then((m) => m.SeatsPage));
const SettingsPage = page(() => import('@/routes/SettingsPage').then((m) => m.SettingsPage));
const SquadPage = page(() => import('@/routes/SquadPage').then((m) => m.SquadPage));
const SquadsPage = page(() => import('@/routes/SquadsPage').then((m) => m.SquadsPage));
const TournamentsPage = page(() =>
  import('@/routes/TournamentsPage').then((m) => m.TournamentsPage),
);
const UserPage = page(() => import('@/routes/UserPage').then((m) => m.UserPage));
const UsersPage = page(() => import('@/routes/UsersPage').then((m) => m.UsersPage));
const VenuePortalPage = page(() =>
  import('@/routes/VenuePortalPage').then((m) => m.VenuePortalPage),
);
const VenuesPage = page(() => import('@/routes/VenuesPage').then((m) => m.VenuesPage));

/** Разделы админки по BLUEPRINT 3.8 и Stage, в котором они наполняются. */
export const NAV_ITEMS: ReadonlyArray<{ path: string; label: string; stage: number }> = [
  { path: '/pool', label: texts.nav.pool, stage: 4 },
  { path: '/squads', label: texts.nav.squads, stage: 4 },
  { path: '/seasons', label: texts.nav.seasons, stage: 5 },
  { path: '/venues', label: texts.nav.venues, stage: 4 },
  { path: '/activities', label: texts.nav.activities, stage: 4 },
  { path: '/proposals', label: texts.nav.proposals, stage: 4 },
  { path: '/users', label: texts.nav.users, stage: 2 },
  { path: '/orders', label: texts.nav.orders, stage: 7 },
  { path: '/notifications', label: texts.nav.notifications, stage: 8 },
  { path: '/flags', label: texts.nav.flags, stage: 9 },
  { path: '/matching', label: texts.nav.matching, stage: 10 },
  { path: '/seats', label: texts.nav.seats, stage: 11 },
  { path: '/tournaments', label: texts.nav.tournaments, stage: 12 },
  { path: '/metrics', label: texts.nav.metrics, stage: 13 },
  { path: '/cities', label: texts.nav.cities, stage: 14 },
  { path: '/companies', label: texts.nav.companies, stage: 14 },
  { path: '/settings', label: texts.nav.settings, stage: 5 },
];

/** Stage, до которого включительно разделы реализованы (остальные — плейсхолдеры). */
export const DONE_STAGE = 14;

const PAGES: Record<string, React.ReactElement> = {
  '/pool': <PoolPage />,
  '/squads': <SquadsPage />,
  '/seasons': <SeasonsPage />,
  '/venues': <VenuesPage />,
  '/activities': <ActivitiesPage />,
  '/proposals': <ProposalsPage />,
  '/users': <UsersPage />,
  '/orders': <OrdersPage />,
  '/notifications': <NotificationsPage />,
  '/flags': <FlagsPage />,
  '/matching': <MatchingPage />,
  '/seats': <SeatsPage />,
  '/tournaments': <TournamentsPage />,
  '/metrics': <MetricsPage />,
  '/cities': <CitiesPage />,
  '/companies': <CompaniesPage />,
  '/settings': <SettingsPage />,
};

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <AuthGuard />,
    children: [
      {
        children: [
          // Портал площадки (Stage 14, задача 2): своя оболочка, разделов админки у роли VENUE нет
          {
            path: '/venue',
            element: <VenueLayout />,
            children: [{ index: true, element: <VenuePortalPage /> }],
          },
          {
            element: <Layout />,
            children: [
              { path: '/', element: <Navigate to="/pool" replace /> },
              ...NAV_ITEMS.map((i) => ({
                path: i.path,
                element: PAGES[i.path] ?? <PlaceholderPage title={i.label} stage={i.stage} />,
              })),
              { path: '/squads/:id', element: <SquadPage /> },
              { path: '/seasons/:id/builder', element: <SeasonBuilderPage /> },
              { path: '/activities/:id', element: <ActivityPage /> },
              { path: '/users/:id', element: <UserPage /> },
              { path: '*', element: <Navigate to="/pool" replace /> },
            ],
          },
        ],
      },
    ],
  },
]);
